/**
 * BAS reference module: the bundled kontoplan (generated by
 * scripts/import-bas.py), class info, K2 report lines, momskoder and dated
 * constants. Read-only; company accounts live in Dexie.
 */
import { BAS_ACCOUNTS, type BasAccountEntry } from './bas';
import { BAS_CLASSES, type BasClassInfo } from './types';
import type { AccountClass, AccountType, EntityType } from '../types';
import { k2LineIdFor as k2LineFromTable } from './report-lines';

export { BAS_ACCOUNTS, BAS_EDITION, SRU_EDITION } from './bas';
export type { BasAccountEntry } from './bas';
export { BAS_CLASSES } from './types';
export type { BasAccount, BasClassInfo } from './types';
export * from './constants';
export * from './momskoder';
export * from './report-lines';

const byNumber: ReadonlyMap<number, BasAccountEntry> = new Map(BAS_ACCOUNTS.map((acc) => [acc.number, acc]));

export function findBasAccount(number: number): BasAccountEntry | undefined {
  return byNumber.get(number);
}

/** Name from the reference, or the number as text when unknown (custom accounts). */
export function basAccountName(number: number): string {
  return byNumber.get(number)?.name ?? String(number);
}

export function isValidAccountNumber(number: number): boolean {
  return Number.isInteger(number) && number >= 1000 && number <= 8999;
}

/** Class of an account number (first digit). Throws for numbers outside 1000-8999. */
export function accountClassOf(number: number): AccountClass {
  if (!isValidAccountNumber(number)) {
    throw new RangeError(`Account number out of BAS range: ${number}`);
  }
  return Math.floor(number / 1000) as AccountClass;
}

export function classInfo(cls: AccountClass): BasClassInfo {
  const info = BAS_CLASSES.find((c) => c.class === cls);
  if (!info) throw new RangeError(`Unknown account class ${cls}`);
  return info;
}

/**
 * Default account type for a number. The reference's own type wins (class 8
 * mixes financial income and costs); otherwise the class decides.
 */
export function defaultTypeFor(number: number): AccountType {
  return byNumber.get(number)?.type ?? classInfo(accountClassOf(number)).type;
}

/** A number is free when it is within BAS range and not defined in the reference. */
export function isFreeBasNumber(number: number): boolean {
  return isValidAccountNumber(number) && !byNumber.has(number);
}

/** BAS marks the account '#' = not to be used under K2. */
export function isNotK2(number: number): boolean {
  return byNumber.get(number)?.notK2 === true;
}

/** K2 report line id for an account and entity (see report-lines.ts). */
export function k2LineFor(number: number, entity: EntityType = 'aktiebolag'): string | undefined {
  return k2LineFromTable(number, entity);
}

/**
 * Search by number prefix or name substring. Accounts flagged notK2 are
 * listed after the rest so the common ones surface first.
 */
export function searchBas(query: string, limit = 50): BasAccountEntry[] {
  const q = query.trim().toLowerCase();
  const pool = q ? BAS_ACCOUNTS.filter((acc) => String(acc.number).startsWith(q) || acc.name.toLowerCase().includes(q)) : BAS_ACCOUNTS;
  const preferred: BasAccountEntry[] = [];
  const rest: BasAccountEntry[] = [];
  for (const acc of pool) {
    (acc.notK2 ? rest : preferred).push(acc);
    if (preferred.length >= limit) break;
  }
  return [...preferred, ...rest].slice(0, limit);
}
